import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';

export interface FileUploadResponse {
  fileName: string;
  fileUrl: string;
  message: string;
  fileSize: number;
  contentType: string;
}

export interface UploadProgress {
  progress: number;
  loaded: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private readonly apiUrl = `${environment.url}api/files`;

  constructor(private http: HttpClient) {}

  /**
   * Sube un archivo al servidor y devuelve la URL
   */
  uploadFile(file: File, category: string = 'general'): Observable<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    return this.http.post<FileUploadResponse>(
      `${this.apiUrl}/upload`,
      formData
    );
  }

  /**
   * Sube un archivo con progreso de carga
   */
  uploadFileWithProgress(
    file: File,
    category: string = 'general'
  ): Observable<HttpEvent<FileUploadResponse> | UploadProgress> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    const req = new HttpRequest('POST', `${this.apiUrl}/upload`, formData, {
      reportProgress: true,
    });

    return this.http.request<FileUploadResponse>(req).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(
              (100 * event.loaded) / (event.total || 1)
            );
            return {
              progress,
              loaded: event.loaded,
              total: event.total || 0,
            } as UploadProgress;
          case HttpEventType.Response:
            return event;
          default:
            return event;
        }
      })
    );
  }

  /**
   * Elimina un archivo del servidor
   */
  deleteFile(fileName: string): Observable<string> {
    return this.http.delete<string>(`${this.apiUrl}/${fileName}`);
  }

  /**
   * Obtiene la URL completa de un archivo
   */
  getFileUrl(fileName: string): string {
    return `${this.apiUrl}/${fileName}`;
  }

  /**
   * Obtiene un archivo como blob con autenticación
   * Útil para mostrar imágenes que requieren token de autorización
   */
  getFileAsBlob(fileName: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${fileName}`, {
      responseType: 'blob',
    });
  }
}
